import { Injectable } from '@angular/core';
import { BlocklyStorageService } from './blockly-storage.service';

/**
 * Zentrale Registry für Recorder-IDs aus DIALOGRECORDER_actions_NEW-Blöcken.
 * Scannt alle Blockly-XMLs des aktuellen Bots und extrahiert die vergebenen Recorder-IDs.
 * Editor-übergreifend nutzbar (Singleton-Service).
 */
@Injectable({
    providedIn: 'root'
})
export class DialogRecorderIdRegistryService {
    private _recorderIds = new Map<string, string>(); // recorderId -> uniqueId (Editor-Quelle)

    constructor(private blocklyStorage: BlocklyStorageService) { }

    /**
     * Scannt alle geladenen Blockly-XMLs nach DIALOGRECORDER_actions_NEW-Blöcken
     * und extrahiert deren recorderId-Felder.
     */
    public refresh(): void {
        this._recorderIds.clear();
        for (const stage of this.blocklyStorage.stages) {
            this._extractRecorderIdsFromXml(stage.xml, stage.uniqueId);
        }
    }

    /**
     * Aktualisiert die Registry mit dem aktuellen XML eines einzelnen Editors.
     * Wird bei jeder Änderung aufgerufen.
     */
    public updateFromEditor(uniqueId: string, xml: string): void {
        // Entferne alte IDs dieses Editors
        for (const [recorderId, sourceId] of this._recorderIds.entries()) {
            if (sourceId === uniqueId) {
                this._recorderIds.delete(recorderId);
            }
        }
        // Extrahiere neue IDs
        this._extractRecorderIdsFromXml(xml, uniqueId);
    }

    /**
     * Gibt alle bekannten Recorder-IDs als Array zurück.
     */
    public getRecorderIds(): string[] {
        return Array.from(this._recorderIds.keys());
    }

    /**
     * Gibt die Recorder-IDs als Blockly-Dropdown-Options zurück.
     * Format: [label, value][]
     */
    public getDropdownOptions(): [string, string][] {
        const ids = this.getRecorderIds();
        if (ids.length === 0) {
            return [['(keine IDs vergeben)', '']];
        }
        return ids.map(id => [id, id]);
    }

    private _extractRecorderIdsFromXml(xml: string, sourceUniqueId: string): void {
        if (!xml) return;
        // Suche nach DIALOGRECORDER_start_NEW-Blöcken und deren recorderId-Feld
        const blockRegex = /<block[^>]*type="DIALOGRECORDER_start_NEW"[^>]*>([\s\S]*?)(?:<\/block>)/g;
        let blockMatch: RegExpExecArray | null;
        while ((blockMatch = blockRegex.exec(xml)) !== null) {
            const blockContent = blockMatch[1] ?? '';
            const fieldRegex = /<field\s+name="recorderId"[^>]*>([^<]*)<\/field>/;
            const fieldMatch = fieldRegex.exec(blockContent);
            if (fieldMatch?.[1]?.trim()) {
                this._recorderIds.set(fieldMatch[1].trim(), sourceUniqueId);
            }
        }
    }
}
